import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { studentApi } from "../../lib/interview/api";
import { getCamMicStream } from "../../lib/interview/media";
import { setSessionCamStream, startSessionWebcamRecording, hasSessionWebcamRecording } from "../../lib/interview/sessionMedia";
import { Button } from "../../components/ui/button";
import { Logo, Overline } from "../../components/himt/Primitives";
import { toast } from "sonner";
import { Camera, IdCard, Rotate3D, CheckCircle2, ArrowRight, Upload, RefreshCw, Loader2, Monitor, HelpCircle, Mic, Play, MapPin } from "lucide-react";
import { getScreenStream, stopAllTracks } from "../../lib/interview/media";

const DIRECTIONS = ["Front", "Left", "Right", "Behind"];

export default function EnvironmentCheck() {
  const { token } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [test, setTest] = useState(null);
  const [stream, setStream] = useState(null);
  const [camOk, setCamOk] = useState(false);
  const [micOk, setMicOk] = useState(false);        // set true after mic-check passes
  const [micTesting, setMicTesting] = useState(false);
  const [micPlayback, setMicPlayback] = useState(null); // dataURL of the 4s test clip
  const [micPeak, setMicPeak] = useState(0);         // max RMS observed during the test
  const [micAttempted, setMicAttempted] = useState(false); // becomes true after first mic test run
  const [micOverride, setMicOverride] = useState(false);   // candidate could not get audio working — proceed anyway (flagged)
  const [idUploaded, setIdUploaded] = useState(false);
  const [idOCR, setIdOCR] = useState(null);
  // GPS / location gate — mandatory (with override) before starting the exam.
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | capturing | ok | denied
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsOverride, setGpsOverride] = useState(false);
  const [idPreviewUrl, setIdPreviewUrl] = useState(null); // data-URL of last captured ID card shown to student
  const [idUploading, setIdUploading] = useState(false);
  const [envCaptured] = useState({});
  // Webcam (4-angle) scan mode was removed — phone QR handoff is now the
  // only supported 360° path (webcam angles were unreliable + duplicated
  // what a phone captures in one sweep).
  const [handoffToken, setHandoffToken] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [phoneReceived, setPhoneReceived] = useState(false);
  // Screen-share capability probe: verifies the candidate's OS/browser can
  // actually complete a getDisplayMedia handshake BEFORE they commit to the
  // exam. Catches the "Share button stays grey" macOS/Sidecar class of bugs
  // (reported by candidate Kirtan S Vakil, Apr 2026) early — when the
  // candidate still has time to fix System Settings and restart Chrome.
  const [screenProbeStatus, setScreenProbeStatus] = useState("idle"); // idle | probing | ok | failed
  const [screenProbeError, setScreenProbeError] = useState(null);
  const [showMacOsHelp, setShowMacOsHelp] = useState(false);
  const pollRef = useRef(null);
  const videoRef = useRef(null);
  const idVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const api = studentApi(token);

  // Callback refs that IMMEDIATELY bind the live stream to each <video>
  // element as soon as it mounts (and re-bind on remount). A useEffect on
  // [stream] isn't enough on some laptops: if a parent re-render remounts
  // the <video>, the new DOM node has no srcObject and the preview stays
  // black. Callback refs fire on every mount AND on every new stream.
  const bindVideoEl = useCallback((el, refBox) => {
    refBox.current = el;
    if (!el) return;
    if (stream && el.srcObject !== stream) {
      try {
        el.srcObject = stream;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (e) { /* noop */ }
    }
  }, [stream]);

  const mainVideoRefCb = useCallback((el) => bindVideoEl(el, videoRef), [bindVideoEl]);
  const idVideoRefCb   = useCallback((el) => bindVideoEl(el, idVideoRef), [bindVideoEl]);

  // Belt-and-braces: also rebind whenever `stream` toggles (covers the case
  // where callback ref fires before stream state is ready).
  useEffect(() => {
    if (!stream) return;
    [videoRef.current, idVideoRef.current].forEach((v) => {
      if (!v) return;
      try {
        if (v.srcObject !== stream) v.srcObject = stream;
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (e) { /* noop */ }
    });
  }, [stream]);

  // High-load auto-deferral: backend returns 503 with a structured detail
  // when the system is at capacity. Show a waiting room with a countdown;
  // the candidate can retry once the timer hits zero.
  const [deferInfo, setDeferInfo] = useState(null);  // { wait_until, wait_minutes, message, active_attempts }
  const [deferRemaining, setDeferRemaining] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/student/attempt/start");
        setAttempt(data);
        // Fetch test (incl. proctoring_config) via the student attempt endpoint
        const { data: full } = await api.get(`/student/attempt/${data.id}`);
        setTest(full.test);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        // 503 + structured high_load_defer payload → render waiting room.
        if (e?.response?.status === 503 && detail && detail.code === "high_load_defer") {
          setDeferInfo(detail);
          return;
        }
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Cannot start attempt");
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Tick down the defer countdown every second; auto-retry when it hits 0.
  useEffect(() => {
    if (!deferInfo?.wait_until) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor(
        (new Date(deferInfo.wait_until).getTime() - Date.now()) / 1000
      ));
      setDeferRemaining(remaining);
      if (remaining <= 0) {
        // Reload to retry the start endpoint (load may still be high; if
        // so the waiting room re-arms with a fresh wait_until).
        window.location.reload();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deferInfo]);

  const pc = test?.proctoring_config || {};
  const requireId = pc.id_ocr !== false;
  const requireEnvScan = pc.env_scan_360 !== false;
  // Screen-share probe is only required when the test has screen_recording
  // enabled. Defaults to true (legacy behaviour) unless explicitly off.
  const requireScreenProbe = pc.screen_recording !== false;
  const isMacOs = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

  const enableCam = async () => {
    // Guard: don't start session recording until we have a real attempt.id,
    // otherwise the ChunkedRecorder starts but chunks have nowhere to go —
    // only the last chunk (i.e. the last ~5s before submit) ends up saved
    // via the fallback path, leading to the "webcam is only 30 seconds"
    // bug reported in WhatsApp (Garish Jerome, Apr 2026).
    if (!attempt?.id) {
      toast.error("Still preparing your exam session — please wait 2 seconds and retry.", { duration: 5000 });
      return;
    }
    try {
      const s = await getCamMicStream();
      const v = s.getVideoTracks();
      if (v.length === 0 || v[0].readyState !== "live") {
        s.getTracks().forEach((t) => t.stop());
        toast.error("Camera permission was denied or no webcam detected. Please allow the camera and retry.", { duration: 8000 });
        return;
      }
      setStream(s);
      // Persist the stream at module level so LiveTest can reuse it without
      // triggering another browser permission prompt.
      setSessionCamStream(s);
      // Start session-long webcam recording the moment the candidate grants
      // permission — covers the entire pre-flight + live test + submit window.
      // Chunks are uploaded every ~5s to /recording-chunk so a tab crash or
      // network drop loses at most a few seconds, not the entire session.
      if ((test?.proctoring_config || {}).webcam_recording !== false) {
        try {
          startSessionWebcamRecording();
        } catch (e) { /* noop */ }
      }
      // The useEffect above binds the stream to BOTH preview video elements
      // as soon as setStream triggers a re-render; no direct assignment needed.
      setCamOk(true);
      toast.success("Camera & microphone enabled. Session recording has started.");
      // Prompt student to complete the mic check
      setTimeout(() => {
        toast("Please complete the Microphone Check below to verify your audio before the test.", {
          duration: 9000,
          icon: null,
          style: { background: "#0A2540", color: "#fff", fontFamily: "monospace", fontSize: "13px" },
        });
      }, 1800);
    } catch (e) {
      const msg = (e && e.name === "NotAllowedError")
        ? "Camera/microphone permission denied. Enable both in your browser site settings and try again."
        : (e && e.name === "NotFoundError")
          ? "No camera or microphone detected. Connect a webcam and retry."
          : "Permission denied for camera/mic.";
      toast.error(msg, { duration: 8000 });
    }
  };

  /**
   * Wait until the main preview video is actually playing real frames
   * (videoWidth > 0 and readyState >= HAVE_CURRENT_DATA). On slow laptops,
   * drawing the video immediately after stream assignment yields a 0×0 /
   * all-black canvas — which is why so many ID captures were failing OCR.
   * Bail out after ~4s so the UI never hangs.
   */
  const waitForVideoReady = async () => {
    const v = videoRef.current;
    if (!v) return false;
    const deadline = Date.now() + 4000;
    while (Date.now() < deadline) {
      if (v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) return true;
      await new Promise((r) => setTimeout(r, 120));
    }
    return v.videoWidth > 0;
  };

  const snapshot = async () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const ready = await waitForVideoReady();
    if (!ready) return null;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return new Promise((resolve) => c.toBlob(resolve, "image/jpeg", 0.92));
  };

  const captureIdFromCamera = async () => {
    if (!camOk) return toast.error("Enable the camera first");
    if (idUploading) return;
    toast.loading("Capturing frame…", { id: "ocr" });
    const blob = await snapshot();
    if (!blob || blob.size < 5000) {
      toast.error("Camera frame was not ready. Hold still and tap Capture again.", { id: "ocr", duration: 6000 });
      return;
    }
    // Immediate local preview so student can see exactly what was captured,
    // even before OCR comes back — they can decide to retake if blurred.
    try {
      if (idPreviewUrl) URL.revokeObjectURL(idPreviewUrl);
    } catch (e) { /* noop */ }
    setIdPreviewUrl(URL.createObjectURL(blob));
    await uploadIdCard(new File([blob], "id_card.jpg", { type: "image/jpeg" }));
  };

  const handleIdFileUpload = (file) => {
    if (!file) return;
    try {
      if (idPreviewUrl) URL.revokeObjectURL(idPreviewUrl);
    } catch (e) { /* noop */ }
    setIdPreviewUrl(URL.createObjectURL(file));
    uploadIdCard(file);
  };

  const retakeId = () => {
    try { if (idPreviewUrl) URL.revokeObjectURL(idPreviewUrl); } catch (e) { /* noop */ }
    setIdPreviewUrl(null);
    setIdOCR(null);
    setIdUploaded(false);
  };

  const uploadIdCard = async (file) => {
    if (!attempt) return;
    setIdUploading(true);
    toast.loading("Uploading ID card…", { id: "ocr" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/student/attempt/${attempt.id}/id-card`, fd);
      setIdOCR(null);
      setIdUploaded(true);
      toast.success("ID card captured.", { id: "ocr" });
    } catch (e) {
      toast.error("ID upload failed — please retake.", { id: "ocr", duration: 6000 });
    } finally {
      setIdUploading(false);
    }
  };

  const captureEnv = async (_direction) => {
    // Deprecated: webcam 4-angle scan removed. Kept as a no-op stub so
    // any lingering callers don't crash; the UI no longer invokes this.
    return null;
  };

  const allEnvDone = !requireEnvScan || phoneReceived;

  const generateQrHandoff = async () => {
    if (!attempt) return;
    try {
      const { data } = await api.post(`/student/attempt/${attempt.id}/env-scan-handoff`);
      setHandoffToken(data.handoff_token);
      const url = `${window.location.origin}/env-scan/${data.handoff_token}`;
      setQrUrl(url);
      // Start polling for status.
      // Aggressive 1.2s poll so the desktop flips to "received" within
      // ~1s of the phone upload finishing — old 2.5s interval felt like
      // a hang to students after a successful phone upload.
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/student/attempt/${attempt.id}/env-scan-status`);
          if (st.received) {
            setPhoneReceived(true);
            clearInterval(pollRef.current);
            toast.success("360° room scan received from phone.");
          }
        } catch (e) {}
      }, 1200);
    } catch (e) {
      toast.error("Could not generate QR code");
    }
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    try { if (idPreviewUrl) URL.revokeObjectURL(idPreviewUrl); } catch (e) { /* noop */ }
  }, [idPreviewUrl]);
  const idOk = !requireId || idUploaded;
  const screenOk = !requireScreenProbe || screenProbeStatus === "ok";
  const gpsResolved = gpsStatus === "ok" || gpsOverride;
  const micResolved = micOk || micOverride;
  const canProceed = camOk && micResolved && idOk && allEnvDone && screenOk && gpsResolved;

  // Location capture — mandatory before the exam. On success we store the
  // coordinates; if the browser/candidate refuses, we record the denial and
  // let them override (no one gets locked out, but the attempt is flagged).
  const requestLocation = useCallback(() => {
    if (!attempt) return;
    if (!navigator.geolocation) {
      setGpsStatus("denied");
      api.post(`/student/attempt/${attempt.id}/gps`, { status: "unavailable", reason: "no geolocation API" }).catch(() => {});
      return;
    }
    setGpsStatus("capturing");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setGpsCoords(c);
        setGpsStatus("ok");
        api.post(`/student/attempt/${attempt.id}/gps`, { ...c, status: "captured" }).catch(() => {});
        toast.success("Location captured.");
      },
      (err) => {
        setGpsStatus("denied");
        api.post(`/student/attempt/${attempt.id}/gps`, { status: "denied", reason: err?.message || "permission denied" }).catch(() => {});
        toast.error("Location access was blocked. Please allow it, or use the override below.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  }, [attempt, api]);

  const confirmGpsOverride = useCallback(() => {
    setGpsOverride(true);
    if (attempt) api.post(`/student/attempt/${attempt.id}/gps`, { status: "denied", reason: "candidate override" }).catch(() => {});
    toast.message("Proceeding without location — this is recorded for the examiner.");
  }, [attempt, api]);

  const confirmMicOverride = useCallback(() => {
    setMicOverride(true);
    if (attempt) api.post(`/student/attempt/${attempt.id}/mic-status`, { status: "override", reason: "candidate could not get mic working" }).catch(() => {});
    toast.message("Proceeding without a verified mic — this is recorded for the examiner.");
  }, [attempt, api]);

  // Mic check — records a 4-second clip from the already-granted session
  // stream, measures peak RMS (so dead / muted / wrong-device mics are
  // caught), and plays the clip back so the candidate HEARS their own
  // voice before the real exam starts. Works for wired, USB, Bluetooth,
  // built-in mics alike — we never block a device, we only verify audio
  // is actually reaching us.
  const runMicTest = async () => {
    if (!stream) return toast.error("Enable the camera first");
    const audioTracks = stream.getAudioTracks ? stream.getAudioTracks() : [];
    if (audioTracks.length === 0) {
      toast.error("No microphone detected on this browser.");
      return;
    }
    setMicTesting(true);
    setMicPlayback(null);
    setMicPeak(0);
    const audioOnly = new MediaStream(audioTracks);
    // Live VU meter via AudioContext (parallel to MediaRecorder).
    let ctx = null, analyser = null, data = null, peak = 0, raf = 0;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(audioOnly);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      data = new Uint8Array(analyser.fftSize);
      src.connect(analyser);
      const loop = () => {
        analyser.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / data.length);
        if (rms > peak) { peak = rms; setMicPeak(peak); }
        raf = requestAnimationFrame(loop);
      };
      loop();
    } catch (_e) { /* AudioContext unavailable — proceed without live VU */ }

    const mime = (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported("audio/webm;codecs=opus"))
      ? "audio/webm;codecs=opus" : "audio/webm";
    const chunks = [];
    let rec;
    try {
      rec = new MediaRecorder(audioOnly, { mimeType: mime });
    } catch (_e) {
      rec = new MediaRecorder(audioOnly);
    }
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
    const done = new Promise((resolve) => { rec.onstop = resolve; });
    rec.start();
    await new Promise((r) => setTimeout(r, 4000));
    try { rec.stop(); } catch (_e) { /* noop */ }
    await done;
    cancelAnimationFrame(raf);
    try { if (ctx && ctx.state !== "closed") ctx.close().catch(() => {}); } catch (_e) {}

    const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
    const url = URL.createObjectURL(blob);
    setMicPlayback(url);
    setMicTesting(false);
    setMicAttempted(true);
    // Threshold: 0.02 RMS ≈ normal conversational speech picked up at
    // arm's length. Below that we consider the mic effectively dead.
    if (peak >= 0.02) {
      setMicOk(true);
      if (attempt) api.post(`/student/attempt/${attempt.id}/mic-status`, { status: "ok" }).catch(() => {});
      toast.success("Microphone detected your voice. Listen back to confirm.");
    } else {
      setMicOk(false);
      toast.error("No voice detected. Speak into the mic and run the test again, or select a different mic from your browser/OS settings.", { duration: 10000 });
    }
  };

  // Clean up object URL on unmount / retest.
  useEffect(() => () => { try { if (micPlayback) URL.revokeObjectURL(micPlayback); } catch (_e) {} }, [micPlayback]);

  const runScreenProbe = async () => {
    setScreenProbeStatus("probing");
    setScreenProbeError(null);
    let probeStream = null;
    // Hard timeout: if the user clicks Share but nothing happens (the
    // macOS-grey-Share bug), getDisplayMedia hangs indefinitely. We surface
    // a macOS help modal after 25s so the candidate isn't stuck.
    const timeoutHandle = setTimeout(() => {
      setScreenProbeStatus("failed");
      setScreenProbeError(
        isMacOs
          ? "Timed out waiting for the Share button. On macOS this usually means Chrome needs Screen Recording permission — see the steps below."
          : "Timed out waiting for you to pick a screen."
      );
      if (isMacOs) setShowMacOsHelp(true);
    }, 25_000);
    try {
      probeStream = await getScreenStream();
      clearTimeout(timeoutHandle);
      // Immediately stop — we only needed proof of capability.
      stopAllTracks(probeStream);
      setScreenProbeStatus("ok");
      toast.success("Screen sharing verified. You can proceed to the test.");
    } catch (err) {
      clearTimeout(timeoutHandle);
      stopAllTracks(probeStream);
      const errName = err?.name || "";
      const errMsg = err?.message || "";
      setScreenProbeStatus("failed");
      // Classify the failure so we can offer the right recovery path.
      if (errName === "ScreenSurfaceError") {
        setScreenProbeError("You picked a tab or a single window. Please click Retry and choose Entire Screen.");
      } else if (errName === "NotAllowedError") {
        setScreenProbeError(
          isMacOs
            ? "Screen sharing was blocked. On macOS: open System Settings → Privacy & Security → Screen Recording → enable Chrome → quit & reopen Chrome → Retry."
            : "You cancelled or blocked screen sharing. Please click Retry and allow it."
        );
        if (isMacOs) setShowMacOsHelp(true);
      } else if (errName === "NotFoundError" || errName === "NotReadableError") {
        setScreenProbeError("Your OS couldn't deliver the screen pixels. On macOS this usually means Chrome lacks Screen Recording permission in System Settings.");
        if (isMacOs) setShowMacOsHelp(true);
      } else {
        setScreenProbeError(errMsg || "Screen sharing failed. Please retry.");
      }
    }
  };

  const proceed = () => {
    // IMPORTANT: do NOT stop the stream — it is stored in sessionMedia and
    // reused by the LiveTest page so the candidate is not prompted twice.
    // The stream and recorder are released on submit via releaseSessionMedia().
    nav(`/exam/${token}/live`);
  };

  // Waiting-room UI takes precedence over everything else when active.
  if (deferInfo) {
    const mins = Math.floor(deferRemaining / 60);
    const secs = deferRemaining % 60;
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6" data-testid="high-load-waiting-room">
        <div className="max-w-xl w-full border-2 border-amber-400 bg-amber-50 rounded-sm p-8 sm:p-10 text-center">
          <Logo className="justify-center inline-flex" />
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 bg-amber-200 rounded-sm">
            <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
            <span className="text-[10px] font-mono font-bold tracking-[0.25em] uppercase text-amber-800">
              System at capacity
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-[#0A2540] text-3xl sm:text-4xl mt-5">
            Please wait — your slot is reserved.
          </h1>
          <p className="text-slate-700 mt-3 leading-relaxed text-sm">
            {deferInfo.message ||
              "The exam server is currently serving the maximum number of concurrent candidates. Your invite link stays valid; we'll let you in automatically once a slot opens up."}
          </p>
          <div className="mt-8 inline-flex flex-col items-center">
            <span className="text-[11px] tracking-[0.25em] uppercase font-bold font-mono text-slate-500">
              Auto-retrying in
            </span>
            <span
              className="font-display font-black text-[#0A2540] text-7xl mt-1 tabular-nums"
              data-testid="high-load-countdown"
            >
              {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
            </span>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 text-left">
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Active candidates</div>
              <div className="font-mono text-2xl font-black text-[#0A2540] mt-1 tabular-nums">
                {deferInfo.active_attempts}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Capacity</div>
              <div className="font-mono text-2xl font-black text-[#0A2540] mt-1 tabular-nums">
                {deferInfo.threshold}
              </div>
            </div>
          </div>
          <p className="text-[11px] font-mono text-slate-500 mt-6 leading-relaxed">
            Keep this tab open. Do not refresh, change networks, or close the browser.
            We'll auto-redirect you into the exam as soon as the timer hits zero and a slot is free.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo />
          <Overline>Pre-test checks</Overline>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display font-black tracking-tighter text-[#0A2540] text-4xl sm:text-5xl">
          Environment & Identity Check
        </h1>
        <p className="text-slate-600 mt-2">
          Complete all three stages to unlock the test.
        </p>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Camera (always on - needed for recording & other checks) */}
          <Step
            number="01"
            icon={Camera}
            title="Enable camera & microphone"
            done={camOk && micResolved}
            testid="step-camera"
            isActive={!camOk || !micResolved}
            nextLabel={requireId ? "Capture your ID card" : requireEnvScan ? "360° room scan" : requireScreenProbe ? "Test screen sharing" : "Enter test"}
          >
            <div className="bg-black aspect-video rounded-sm overflow-hidden">
              <video ref={mainVideoRefCb} muted playsInline autoPlay className="w-full h-full object-cover" data-testid="env-webcam-preview" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            {!camOk ? (
              <Button onClick={enableCam} className="w-full mt-4 bg-[#0A2540] text-white rounded-sm" data-testid="enable-cam-btn">
                <Camera size={16} className="mr-2" /> Enable Camera
              </Button>
            ) : (
              <>
                <div className="mt-4 flex items-center gap-2 text-sm text-green-700 font-bold">
                  <CheckCircle2 size={16} /> Camera & mic active
                </div>
                {hasSessionWebcamRecording() && (
                  <div className="mt-2 flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-red-600" data-testid="session-recording-banner">
                    <span className="h-2 w-2 rounded-full bg-red-500 recording-pulse inline-block" />
                    Session recording · running
                  </div>
                )}

                {/* Mic check — MUST pass before the candidate can proceed.
                    Surfaces a dead / muted / wrong-device mic before the
                    exam instead of at playback time. Works with wired,
                    USB, Bluetooth, and built-in mics — we only verify
                    audio is reaching us, never block a device type. */}
                <div className="mt-4 border border-slate-200 rounded-sm p-3" data-testid="mic-check-panel">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-bold text-[#0A2540]">
                      <Mic size={14} /> Mic Check
                      {micOk && <CheckCircle2 size={14} className="text-green-600" />}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={runMicTest}
                      disabled={micTesting}
                      className="rounded-sm text-xs"
                      data-testid="mic-test-btn"
                    >
                      {micTesting ? (
                        <><Loader2 size={12} className="mr-1 animate-spin" /> Recording 4s…</>
                      ) : (
                        <>{micOk ? "Redo Mic Test" : "Test My Mic"}</>
                      )}
                    </Button>
                  </div>
                  {/* Live VU meter (while recording) */}
                  {micTesting && (
                    <div className="mt-3">
                      <div className="h-2 w-full bg-slate-100 rounded-sm overflow-hidden">
                        <div
                          className={`h-full transition-all duration-100 ${micPeak >= 0.02 ? "bg-green-500" : "bg-amber-400"}`}
                          style={{ width: `${Math.min(100, Math.round(micPeak * 400))}%` }}
                          data-testid="mic-vu-meter"
                        />
                      </div>
                      <div className="text-[11px] font-mono text-slate-500 mt-1">
                        Speak clearly: "Testing, one, two, three"
                      </div>
                    </div>
                  )}
                  {/* Result + playback */}
                  {!micTesting && micPlayback && (
                    <div className="mt-3 space-y-2">
                      <audio src={micPlayback} controls className="w-full" data-testid="mic-playback" />
                      {micOk ? (
                        <div className="flex items-start gap-2 text-xs text-green-700">
                          <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                          <span>Voice captured — peak level {Math.round(micPeak * 100)}%. Listen to confirm your voice is clear.</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 text-xs text-red-700">
                          <span className="font-bold">✕</span>
                          <span>
                            Very low audio (peak {Math.round(micPeak * 100)}%).
                            Speak louder or select a different microphone in your browser's
                            address-bar mic icon or OS sound settings, then press <strong>Redo Mic Test</strong>.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {!micTesting && !micPlayback && (
                    <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                      Records 4 seconds of audio and plays it back so you can confirm your mic (any type — wired, USB, Bluetooth, built-in) is capturing clearly before the exam starts.
                    </div>
                  )}
                  {/* Mic override — candidates whose mic simply will not work
                      were getting stuck unable to start the exam. Mirrors the
                      GPS override: they can proceed, but it's flagged for the
                      examiner and recorded on the attempt. Only shown after a
                      failed attempt, and hidden once the mic passes. */}
                  {!micTesting && micAttempted && !micOk && !micOverride && (
                    <div className="mt-3 border-t border-slate-200 pt-3">
                      <p className="text-[11px] text-slate-500 leading-snug mb-2">
                        Still can't get your microphone working? You may proceed without it — this will be <strong>recorded and flagged</strong> for the examiner.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={confirmMicOverride}
                        className="rounded-sm text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                        data-testid="mic-override-btn"
                      >
                        Proceed without mic (flagged)
                      </Button>
                    </div>
                  )}
                  {micOverride && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 font-bold" data-testid="mic-override-confirmed">
                      <CheckCircle2 size={14} /> Proceeding without a verified mic (flagged for examiner).
                    </div>
                  )}
                </div>
              </>
            )}
          </Step>

          {/* ID Card — capture only (OCR verification removed per admin request) */}
          {requireId && (
            <Step
              number="02"
              icon={IdCard}
              title="Capture your ID card"
              done={idUploaded}
              testid="step-id"
              disabled={!camOk}
              isActive={camOk && !idUploaded}
              nextLabel={requireEnvScan ? "360° room scan (phone QR)" : requireScreenProbe ? "Test screen sharing" : "Enter test"}
            >
            <div className="space-y-3">
              {/* Live framing preview with dashed overlay */}
              <div className="relative bg-black aspect-video rounded-sm overflow-hidden">
                <video
                  ref={idVideoRefCb}
                  playsInline muted autoPlay
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="id-capture-preview"
                />
                {/* Dashed framing guide */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="border-2 border-dashed border-white/80 rounded-md w-[78%] aspect-[1.586/1] relative">
                    <span className="absolute -top-3 left-3 bg-[#0A2540] text-white text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-sm">
                      HOLD ID HERE
                    </span>
                    <span className="absolute -bottom-3 right-3 bg-white text-[#0A2540] text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border border-slate-300">
                      ID-CARD · 1.586:1
                    </span>
                  </div>
                </div>
                {!camOk && (
                  <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center text-xs text-white font-mono">
                    Enable the camera first
                  </div>
                )}
              </div>

              {/* Captured ID preview — shown after the student taps Capture
                  so they can *see* what the examiner will receive and retake
                  if blurred / glare / cropped. */}
              {idPreviewUrl && (
                <div className="bg-slate-50 border border-slate-300 rounded-sm p-3" data-testid="captured-id-preview">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-600 font-bold">
                      Captured · review for clarity
                    </span>
                    {idUploading && <Loader2 size={12} className="animate-spin text-[#2563EB]" />}
                  </div>
                  <img
                    src={idPreviewUrl}
                    alt="Captured ID"
                    className="w-full rounded-sm border border-slate-300 bg-black"
                    data-testid="captured-id-image"
                  />
                  <div className="text-[11px] text-slate-600 mt-2 leading-snug">
                    If the name or photo is blurry, glared, or cut off, tap <b>Retake</b> and hold the ID steady inside the dashed frame.
                  </div>
                  <button
                    onClick={retakeId}
                    disabled={idUploading}
                    className="mt-2 w-full text-xs font-bold tracking-wider uppercase py-2 rounded-sm border border-slate-400 bg-white hover:bg-slate-50 disabled:opacity-50"
                    data-testid="retake-id-btn"
                  >
                    <RefreshCw size={12} className="inline mr-1" /> Retake ID
                  </button>
                </div>
              )}

              {/* ID capture status (OCR removed — capture-only) */}
              {idUploaded && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-xs">
                  <div className="font-bold text-emerald-700">ID card captured ✓</div>
                  <div className="text-slate-600 mt-1 font-mono">
                    Your ID photo has been saved as exam evidence. You can proceed.
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={captureIdFromCamera}
                  disabled={!camOk || idUploading}
                  className="flex-1 bg-[#2563EB] text-white rounded-sm disabled:opacity-60"
                  data-testid="capture-id-camera-btn"
                >
                  {idUploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Camera size={14} className="mr-1" />}
                  {idUploading ? "Working…" : (idPreviewUrl ? "Recapture" : "Capture from Camera")}
                </Button>
                <label className="flex-1">
                  <input
                    type="file" accept="image/*" capture="environment"
                    className="hidden" disabled={!camOk || idUploading}
                    onChange={(e) => handleIdFileUpload(e.target.files[0])}
                    data-testid="id-upload-input"
                  />
                  <span className={`w-full block text-center cursor-pointer px-4 py-2 border border-slate-300 rounded-sm text-sm font-bold ${(!camOk || idUploading) ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}>
                    <Upload size={14} className="inline mr-1" /> Upload photo
                  </span>
                </label>
              </div>
              <p className="text-[10px] text-slate-500 font-mono text-center">
                Hold your ID inside the dashed box · photo saved as exam evidence
              </p>
            </div>
            </Step>
          )}

          {/* 360 scan — gated by pc.env_scan_360. Phone-QR only (webcam
              4-angle mode removed — phones produce a far better scan in
              one sweep and every candidate is required to have one). */}
          {requireEnvScan && (
            <Step
              number={requireId ? "03" : "02"}
              icon={Rotate3D}
              title="360° environment scan"
              done={allEnvDone}
              testid="step-360"
              disabled={!camOk}
              isActive={camOk && (!requireId || idUploaded) && !allEnvDone}
              nextLabel={requireScreenProbe ? "Test screen sharing" : "Enter the test"}
            >
            <div className="flex items-center gap-2 mb-4 p-2.5 bg-amber-50 border border-amber-300 rounded-sm text-xs font-mono text-amber-900">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              <span><b>Smartphone required</b> — scan the QR code below to complete this step.</span>
            </div>

            <div className="text-sm text-slate-700">
                {!qrUrl ? (
                  <>
                    <p>Scan a QR code with your phone, record a 10-second 360° room sweep, and the video will auto-upload here.</p>
                    <Button
                      onClick={generateQrHandoff}
                      disabled={!camOk || !attempt}
                      className="mt-4 bg-[#2563EB] text-white rounded-sm w-full"
                      data-testid="generate-qr-btn"
                    >
                      <Rotate3D size={14} className="mr-1" /> Generate QR code
                    </Button>
                  </>
                ) : phoneReceived ? (
                  <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-4 text-center">
                    <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
                    <div className="font-bold text-emerald-700 mt-2">360° room scan received ✓</div>
                    <div className="text-xs font-mono text-slate-500 mt-1">You can proceed to the live test.</div>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center">
                    <img
                      alt="Scan with phone"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&data=${encodeURIComponent(qrUrl)}`}
                      className="mx-auto rounded-sm bg-white p-2 border border-slate-200"
                      data-testid="qr-image"
                    />
                    <div className="text-xs font-mono text-slate-500 mt-3 break-all px-2">{qrUrl}</div>
                    <div className="mt-3 text-sm text-slate-700">
                      <span className="recording-pulse inline-block w-2 h-2 rounded-full bg-amber-500 mr-2"></span>
                      Waiting for phone upload…
                    </div>
                    <ol className="text-left text-xs font-mono text-slate-600 mt-4 pl-5 list-decimal space-y-1">
                      <li>Open your phone's camera and scan this QR.</li>
                      <li>Tap the link → Allow camera → Start 360° Scan.</li>
                      <li>Slowly rotate your body 360° over 10 seconds.</li>
                      <li>This page will update automatically.</li>
                    </ol>
                  </div>
                )}
              </div>
          </Step>
          )}

          {/* Screen-share probe — catches the macOS Sidecar/permission class
              of failures BEFORE the candidate commits to the exam. If this
              step passes, the live-test Share click will too. */}
          {requireScreenProbe && (
            <Step
              number={requireId && requireEnvScan ? "04" : requireId || requireEnvScan ? "03" : "02"}
              icon={Monitor}
              title="Test screen sharing"
              done={screenProbeStatus === "ok"}
              testid="step-screen"
              disabled={!camOk || (requireEnvScan && !phoneReceived) || (requireId && !idUploaded)}
              isActive={camOk && idOk && allEnvDone && screenProbeStatus !== "ok"}
              nextLabel="Enter the test"
            >
              <p className="text-sm text-slate-700">
                Click below to run a quick screen-share dry-run. Your browser will open the same dialog you'll see on the exam — pick <b>Entire Screen</b> and click <b>Share</b>. The capture stops immediately; we only need to prove your OS can deliver the pixels.
              </p>

              {isMacOs && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-sm text-xs font-mono text-amber-900">
                  <b>macOS detected.</b> If the <b>Share</b> button stays grey, you need to grant <b>Screen Recording</b> permission:{" "}
                  <button
                    type="button"
                    className="underline font-bold hover:text-amber-700"
                    onClick={() => setShowMacOsHelp(true)}
                    data-testid="macos-help-link"
                  >
                    show instructions
                  </button>
                </div>
              )}

              {screenProbeStatus === "failed" && screenProbeError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-sm text-sm text-red-800" data-testid="screen-probe-error">
                  {screenProbeError}
                </div>
              )}

              {screenProbeStatus === "ok" ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-green-700 font-bold">
                  <CheckCircle2 size={16} /> Screen sharing verified — your OS is correctly configured.
                </div>
              ) : (
                <Button
                  onClick={runScreenProbe}
                  disabled={!camOk || screenProbeStatus === "probing"}
                  className="mt-4 w-full bg-[#2563EB] text-white rounded-sm"
                  data-testid="screen-probe-btn"
                >
                  {screenProbeStatus === "probing"
                    ? <><Loader2 size={14} className="mr-1 animate-spin" /> Waiting for you to pick a screen…</>
                    : <><Monitor size={14} className="mr-1" /> {screenProbeStatus === "failed" ? "Retry screen test" : "Run screen-share test"}</>}
                </Button>
              )}
            </Step>
          )}

          {/* Mandatory Location check (with override) */}
          <Step
            number="LOC"
            icon={MapPin}
            title="Location check"
            done={gpsResolved}
            testid="step-location"
            disabled={!camOk}
            isActive={camOk && idOk && allEnvDone && screenOk && !gpsResolved}
            nextLabel="Enter the test"
          >
            <p className="text-sm text-slate-700">
              We need to record your location for exam integrity. Click below and choose <b>Allow</b> when your browser asks for location access.
            </p>
            {gpsStatus === "ok" && (
              <div className="mt-4 flex items-center gap-2 text-sm text-green-700 font-bold" data-testid="gps-ok">
                <CheckCircle2 size={16} /> Location captured{gpsCoords ? ` (±${Math.round(gpsCoords.accuracy || 0)}m)` : ""}.
              </div>
            )}
            {gpsStatus !== "ok" && (
              <Button
                onClick={requestLocation}
                disabled={!camOk || gpsStatus === "capturing"}
                className="mt-4 w-full bg-[#2563EB] text-white rounded-sm"
                data-testid="gps-allow-btn"
              >
                {gpsStatus === "capturing"
                  ? <><Loader2 size={14} className="mr-1 animate-spin" /> Getting your location…</>
                  : <><MapPin size={14} className="mr-1" /> {gpsStatus === "denied" ? "Retry location access" : "Allow location access"}</>}
              </Button>
            )}
            {gpsStatus === "denied" && !gpsOverride && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-sm text-xs text-amber-900" data-testid="gps-denied-help">
                <p className="font-bold">Couldn't get your location.</p>
                <p className="mt-1">Enable location for this site in your browser and tap "Retry". If your device genuinely cannot share location, you may proceed — but this will be flagged to the examiner.</p>
                <Button
                  onClick={confirmGpsOverride}
                  variant="outline"
                  className="mt-3 rounded-sm border-amber-500 text-amber-800 hover:bg-amber-100"
                  data-testid="gps-override-btn"
                >
                  I cannot share location — proceed anyway
                </Button>
              </div>
            )}
            {gpsOverride && (
              <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 font-bold" data-testid="gps-override-confirmed">
                <CheckCircle2 size={16} /> Proceeding without location (flagged for examiner).
              </div>
            )}
          </Step>
        </div>

        {showMacOsHelp && (
          <MacOsScreenHelp onClose={() => setShowMacOsHelp(false)} />
        )}

        <div className="mt-12 p-8 bg-[#0A2540] rounded-sm flex items-center justify-between text-white">
          <div>
            <Overline className="text-white/70">Security acknowledgement</Overline>
            <p className="mt-2 max-w-xl">
              I agree to the rules of examination, including lockdown browser, continuous recording and AI-based proctoring.
            </p>
          </div>
          <Button
            onClick={proceed}
            disabled={!canProceed}
            className="bg-white text-[#0A2540] hover:bg-white/90 rounded-sm px-8 py-6 text-base font-bold disabled:opacity-50"
            data-testid="proceed-to-test-btn"
            title={!canProceed ? (
              !camOk ? "Enable your camera first" :
              !micResolved ? "Run the Mic Check first — we need to verify your voice is captured (or use the override)" :
              !idOk ? "Capture your ID card first" :
              !allEnvDone ? "Complete the 360° room scan first" :
              !screenOk ? "Test screen sharing first" :
              !gpsResolved ? "Allow location access (or use the override) first" : ""
            ) : ""}
          >
            Enter Test <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
}

function Step({ number, icon: Icon, title, done, children, disabled, testid, nextLabel, isActive }) {
  return (
    <div
      className={`relative border rounded-sm p-6 bg-white ${done ? "border-green-500" : isActive ? "border-[#2563EB] ring-2 ring-[#2563EB]/20" : disabled ? "border-slate-200 opacity-60" : "border-slate-300"}`}
      data-testid={testid}
    >
      {isActive && !done && (
        <div
          className="absolute -top-3 left-4 bg-[#2563EB] text-white text-[10px] font-mono font-black tracking-[0.2em] uppercase px-2 py-1 rounded-sm flex items-center gap-1 animate-pulse"
          data-testid={`${testid}-active-pill`}
        >
          <ArrowRight size={10} /> Do this now
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 flex items-center justify-center rounded-sm font-mono font-black text-xs ${done ? "bg-green-600 text-white" : "bg-[#0A2540] text-white"}`}>
          {done ? <CheckCircle2 size={14} /> : number}
        </div>
        <div className="font-display font-bold text-[#0A2540] tracking-tight">{title}</div>
        <Icon size={16} className="ml-auto text-slate-400" />
      </div>
      <div className="mt-5">{children}</div>
      {done && nextLabel && (
        <div
          className="mt-5 pt-4 border-t border-dashed border-green-300 flex items-center gap-2 text-xs font-mono font-bold tracking-wider uppercase text-green-700"
          data-testid={`${testid}-next-hint`}
        >
          <CheckCircle2 size={12} /> Done
          <ArrowRight size={12} className="ml-1" />
          <span className="text-[#2563EB]">Next: {nextLabel}</span>
        </div>
      )}
    </div>
  );
}


// macOS-specific help overlay shown when the screen-share probe hangs or
// rejects. Walks the candidate through the exact System Settings pane they
// need to open so they can self-serve the fix without contacting support.
function MacOsScreenHelp({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
      onClick={onClose}
      data-testid="macos-help-modal"
    >
      <div
        className="bg-white max-w-xl w-full rounded-sm p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-slate-400 hover:text-slate-700 text-2xl leading-none"
          data-testid="macos-help-close"
        >
          ×
        </button>
        <div className="flex items-center gap-3">
          <HelpCircle size={28} className="text-[#2563EB]" />
          <h2 className="font-display font-black tracking-tight text-2xl text-[#0A2540]">
            macOS: enable Screen Recording for Chrome
          </h2>
        </div>
        <p className="text-sm text-slate-600 mt-3">
          If the Share button stays grey or nothing happens after you click it, macOS is blocking the pixel stream. Follow these steps exactly:
        </p>
        <ol className="mt-5 space-y-3 text-sm text-slate-800 list-decimal pl-6">
          <li>Open <b>Apple menu</b> (🍎 top-left) → <b>System Settings</b>.</li>
          <li>In the sidebar, click <b>Privacy & Security</b>.</li>
          <li>Scroll down and click <b>Screen Recording</b>.</li>
          <li>
            Find <b>Google Chrome</b> in the list and <b>turn the toggle ON</b>. If Chrome isn't listed, click the <b>+</b> button, navigate to <code className="bg-slate-100 px-1 rounded">Applications → Google Chrome</code>, and add it.
          </li>
          <li>
            macOS will prompt: <i>"Google Chrome must quit and reopen"</i> — click <b>Quit &amp; Reopen</b> (this step is mandatory, the toggle does nothing until Chrome fully restarts).
          </li>
          <li>After Chrome reopens, come back to this exam link and click <b>Retry screen test</b>.</li>
        </ol>
        <div className="mt-6 p-3 bg-amber-50 border border-amber-300 rounded-sm text-xs font-mono text-amber-900">
          <b>Common gotcha:</b> if you have <b>Sidecar</b> (iPad as Mac display), <b>AirPlay receiver</b>, or <b>display mirroring</b> on, disconnect those before retrying. Chrome sometimes can't share virtual displays.
        </div>
        <button
          onClick={onClose}
          className="mt-6 w-full bg-[#0A2540] text-white rounded-sm py-3 font-bold"
          data-testid="macos-help-done"
        >
          Got it — I'll try again
        </button>
      </div>
    </div>
  );
}
